import { create } from 'zustand';
import type { BuilderObjective, BuilderAnchorMode } from '../api/client.js';
import type { GlobalCurrency } from '../currency.js';

// Per-panel Platform Builder input state, kept in a module-level store so it
// SURVIVES the panel unmounting — each workspace tab mounts its own dockview, so
// switching tabs and back would otherwise reset all the local useState to
// defaults (the "panel goes clean" bug). Keyed by panelId. Session-only (not
// persisted to localStorage); the panel seeds its useState from here on mount
// and syncs back on change.

export interface BuilderInputs {
  targetInput: string;
  revCcy: GlobalCurrency | '';
  country: string;
  subsegment: string;
  region: string;
  objectives: BuilderObjective[];
  anchorMode: BuilderAnchorMode;
  anchorSize: string;
  anchorUnit: 'pct' | 'abs';
  /** Operator-size band key (revenue-per-site); 'any' = no filter. */
  sizeBand: string;
  /** Constraint: exclude the bottom regulatory-quality band. */
  excludeWeakQuality: boolean;
  /** Aggregation sizing intent — 'auto' (pool median), 'count' (# of targets), or
   *  'size' (typical operator size, build currency millions). */
  aggSizeMode: 'auto' | 'count' | 'size';
  aggSizeValue: string;
  platformEntityId: string | null;
  /** Targets the user asked to replace (excluded from the build), carried WITH
   *  their name so each one can be individually restored (not just reset all). */
  excluded: ReplacedTarget[];
}

export interface ReplacedTarget {
  id: string;
  name: string | null;
}

interface BuilderStateStore {
  byPanel: Record<string, BuilderInputs>;
  set: (panelId: string, inputs: BuilderInputs) => void;
}

export const useBuilderState = create<BuilderStateStore>((set) => ({
  byPanel: {},
  set: (panelId, inputs) => set((s) => ({ byPanel: { ...s.byPanel, [panelId]: inputs } })),
}));
